/**
 * LevelCompleteModal — shown when the Cambrian biomass goal is reached.
 * Stops play, shows the run's stats and the body you evolved, and offers
 * the next level (disabled while there is no stage after this one).
 */

import { MAX_PART_LEVEL, PARTS, partIconSvg, partLevel, type PartLevels } from './parts';

export interface LevelStats {
  time: number;
  biomass: number;
  killsByTier: Record<number, number>;
  deaths: number;
  evolutions: number;
  apexSlain: boolean;
  parts: PartLevels;
}

export interface LevelCompleteActions {
  /** Null when there's no next level yet. */
  onNext: (() => void) | null;
  onKeepPlaying: () => void;
  onMenu: () => void;
}

export class LevelCompleteModal {
  private root: HTMLElement | null = null;

  open(s: LevelStats, a: LevelCompleteActions): void {
    this.injectStyle();
    const mm = Math.floor(s.time / 60);
    const ss = String(Math.floor(s.time % 60)).padStart(2, '0');
    const eaten = Object.values(s.killsByTier).reduce((x, y) => x + y, 0);
    const tierNames: Record<number, string> = { 1: 'Plankton', 2: 'Arthropods', 3: 'Hunters', 4: 'Apex' };
    const tiers = [1, 2, 3, 4].map((t) =>
      `<div class="lc-tier"><span>${tierNames[t]}</span><b>${(s.killsByTier[t] ?? 0).toLocaleString()}</b></div>`).join('');
    const grown = PARTS.filter((p) => partLevel(s.parts, p.id) > 0);
    const parts = grown.length === 0
      ? '<div class="lc-none">No parts grown — a pure hunter.</div>'
      : grown.map((p) => {
          const lv = partLevel(s.parts, p.id);
          const pips = Array.from({ length: MAX_PART_LEVEL }, (_, i) => `<i class="${i < lv ? 'on' : ''}"></i>`).join('');
          return `<div class="lc-part" style="--mc:#${p.color.toString(16).padStart(6, '0')}"><div class="lc-picon">${partIconSvg(p.id, p.color)}</div><div class="lc-pname">${p.name}</div><div class="lc-pips">${pips}</div></div>`;
        }).join('');

    const root = document.createElement('div');
    root.id = 'cambrian-complete';
    root.innerHTML = `
      <div class="lc-scrim"></div>
      <div class="lc-modal">
        <div class="lc-sub">LEVEL 3 · CAMBRIAN SEA</div>
        <div class="lc-title">LEVEL COMPLETE</div>
        <div class="lc-blurb">The sea is yours. Something bigger is waiting beyond it.</div>
        <div class="lc-stats">
          <div class="lc-stat"><div class="lc-l">TIME</div><div class="lc-v">${mm}:${ss}</div></div>
          <div class="lc-stat"><div class="lc-l">BIOMASS</div><div class="lc-v">${Math.floor(s.biomass).toLocaleString()}</div></div>
          <div class="lc-stat"><div class="lc-l">EATEN</div><div class="lc-v">${eaten.toLocaleString()}</div></div>
          <div class="lc-stat"><div class="lc-l">EVOLUTIONS</div><div class="lc-v">${s.evolutions}</div></div>
          <div class="lc-stat"><div class="lc-l">DEATHS</div><div class="lc-v">${s.deaths}</div></div>
          <div class="lc-stat"><div class="lc-l">APEX</div><div class="lc-v">${s.apexSlain ? 'SLAIN' : 'ALIVE'}</div></div>
        </div>
        <div class="lc-section">Eaten by size</div>
        <div class="lc-tiers">${tiers}</div>
        <div class="lc-section">Your body</div>
        <div class="lc-parts">${parts}</div>
        <div class="lc-actions">
          <button class="lc-btn lc-next"${a.onNext ? '' : ' disabled'}>${a.onNext ? 'Next level →' : 'Next level — coming soon'}</button>
          <button class="lc-btn lc-keep">Keep hunting</button>
          <button class="lc-btn lc-menu">Main menu</button>
        </div>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;
    const on = (sel: string, fn: () => void) => root.querySelector(sel)?.addEventListener('click', () => { this.close(); fn(); });
    if (a.onNext) on('.lc-next', a.onNext);
    on('.lc-keep', a.onKeepPlaying);
    on('.lc-menu', a.onMenu);
  }

  close(): void {
    this.root?.remove();
    this.root = null;
  }

  private injectStyle(): void {
    if (document.getElementById('cambrian-complete-styles')) return;
    const style = document.createElement('style');
    style.id = 'cambrian-complete-styles';
    style.textContent = `
#cambrian-complete { position: fixed; inset: 0; z-index: 220; font-family: 'Inter', system-ui, sans-serif; color: #fff5dc; display: flex; align-items: center; justify-content: center; }
#cambrian-complete .lc-scrim { position: absolute; inset: 0; background: rgba(4, 10, 14, 0.7); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
#cambrian-complete .lc-modal { position: relative; width: min(720px, calc(100vw - 32px)); max-height: calc(100vh - 32px); overflow: auto; box-sizing: border-box; padding: 28px 30px; border-radius: 18px; background: rgba(18, 10, 4, 0.95); border: 1px solid rgba(255, 208, 112, 0.35); box-shadow: 0 30px 70px rgba(0,0,0,0.6), 0 0 60px rgba(255, 190, 90, 0.12); text-align: center; animation: lc-in 0.5s ease-out; }
@keyframes lc-in { from { transform: translateY(16px) scale(0.97); opacity: 0; } to { transform: none; opacity: 1; } }
#cambrian-complete .lc-sub { font-size: 11px; letter-spacing: 0.4em; opacity: 0.55; }
#cambrian-complete .lc-title { font-size: 38px; font-weight: 900; letter-spacing: 0.14em; color: #ffd070; text-shadow: 0 0 24px rgba(255, 190, 90, 0.5); margin-top: 4px; }
#cambrian-complete .lc-blurb { font-size: 13px; opacity: 0.75; margin-top: 6px; }
#cambrian-complete .lc-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 20px; }
#cambrian-complete .lc-stat { padding: 12px 8px; border-radius: 10px; background: rgba(255, 220, 160, 0.06); border: 1px solid rgba(255, 220, 160, 0.12); }
#cambrian-complete .lc-l { font-size: 9px; letter-spacing: 0.24em; opacity: 0.55; }
#cambrian-complete .lc-v { font-size: 22px; font-weight: 800; margin-top: 4px; font-variant-numeric: tabular-nums; }
#cambrian-complete .lc-section { font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase; opacity: 0.55; margin: 20px 0 8px; }
#cambrian-complete .lc-tiers { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
#cambrian-complete .lc-tier { display: flex; flex-direction: column; gap: 2px; font-size: 11px; padding: 8px; border-radius: 8px; background: rgba(0,0,0,0.25); }
#cambrian-complete .lc-tier b { font-size: 16px; color: #ffd070; }
#cambrian-complete .lc-parts { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
#cambrian-complete .lc-part { width: 86px; padding: 8px 4px; border-radius: 10px; border: 1px solid var(--mc); background: rgba(0,0,0,0.3); display: flex; flex-direction: column; align-items: center; gap: 4px; }
#cambrian-complete .lc-picon { width: 34px; height: 34px; }
#cambrian-complete .lc-pname { font-size: 10px; font-weight: 700; }
#cambrian-complete .lc-pips { display: flex; gap: 3px; }
#cambrian-complete .lc-pips i { width: 6px; height: 6px; border-radius: 50%; border: 1px solid var(--mc); }
#cambrian-complete .lc-pips i.on { background: var(--mc); }
#cambrian-complete .lc-none { font-size: 12px; opacity: 0.6; }
#cambrian-complete .lc-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 24px; }
#cambrian-complete .lc-btn { font: inherit; font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; padding: 12px 22px; border-radius: 999px; cursor: pointer; border: 1px solid rgba(255,255,255,0.18); background: rgba(255,255,255,0.05); color: #fff5dc; }
#cambrian-complete .lc-btn:hover:not(:disabled) { background: rgba(255,255,255,0.12); }
#cambrian-complete .lc-next { background: linear-gradient(180deg, #ffd070, #e8a040); color: #2a1404; border: none; box-shadow: 0 0 20px rgba(255, 190, 90, 0.4); }
#cambrian-complete .lc-next:hover:not(:disabled) { background: linear-gradient(180deg, #ffe090, #f0b050); }
#cambrian-complete .lc-next:disabled { cursor: not-allowed; opacity: 0.55; box-shadow: none; }
@media (max-width: 560px), (max-height: 500px) { #cambrian-complete .lc-stats { grid-template-columns: repeat(2, 1fr); } #cambrian-complete .lc-tiers { grid-template-columns: repeat(2, 1fr); } }
`;
    document.head.appendChild(style);
  }
}
